/**
 * Retrieves the access token from localStorage.
 *
 * @returns The access token as a string, or `null` if not found.
 */
const getAccessToken = (): string | null => {
  return localStorage.getItem('accessDarwinToken');
};

type Metadata = {
  limit: number;
  page: number;
  total: number;
  totalPages: number;
  archivedCount?: number;
};

export interface ApiResponse<T> {
  code: number;
  data: T;
  message: string;
  metadata?: Metadata;
}

/**
 * Generic function to fetch data from an API.
 *
 * @template T - The expected shape of the response data.
 * @param {string} url - The API endpoint to fetch data from.
 * @param {RequestInit} [options={}] - Optional fetch configurations such as method, headers, body, etc.
 * @returns {Promise<ApiResponse<T>>} A promise that resolves to an `ApiResponse` containing data of type `T`.
 * @throws {Error} Throws an error if the network response is not ok (i.e., `response.ok` is `false`).
 *
 * @example
 * ```typescript
 * interface User {
 *   id: number;
 *   name: string;
 * }
 *
 * const fetchUser = async () => {
 *   const response = await fetchFromApi<User>('/api/user/1');
 *   console.log(response.data.name);
 * };
 * ```
 */
export const fetchFromApi = async <T = any>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const accessToken = getAccessToken();
  const headers: HeadersInit = {
    ...(accessToken && { Authorization: accessToken }),
    ...(options.body instanceof FormData
      ? {}
      : { 'Content-Type': 'application/json' }),
    ...options.headers, // Allow overriding headers if needed
  };
  const response = await fetch(url, {
    ...options,
    headers,
  });
  if (!response.ok) {
    // if (response.status === 401) {
    //   const logOutEvent = new CustomEvent(EVENTS.logout);
    //   document.dispatchEvent(logOutEvent);
    // return true;
    // }
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = null; // Handle case where there's no JSON body
    }
    // eslint-disable-next-line no-throw-literal
    throw {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      body: errorData, // The error body (if present)
    };
  }
  const data: ApiResponse<T> = (await response.json()) as ApiResponse<T>;
  return data;
};

/**
 * Function to fetch blob data (files) from an API.
 *
 * @param {string} url - The API endpoint to fetch the blob from.
 * @param {RequestInit} [options={}] - Optional fetch configurations.
 * @param {boolean} [skipAuth=false] - Whether to skip adding the Authorization header.
 * @returns {Promise<Blob>} A promise that resolves to a Blob.
 * @throws {Error} Throws an error if the network response is not ok.
 *
 * @example
 * ```typescript
 * const downloadFile = async () => {
 *   const blob = await fetchBlobFromApi('/api/file/download');
 *   downloadBlob(blob, 'filename.pdf');
 * };
 * ```
 */
export const fetchBlobFromApi = async (
  url: string,
  options: RequestInit = {},
  skipAuth: boolean = false
): Promise<Blob> => {
  const accessToken = getAccessToken();
  const headers: HeadersInit = {
    ...(accessToken && !skipAuth && { Authorization: accessToken }),
    ...options.headers, // Allow overriding headers if needed
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = null; // Handle case where there's no JSON body
    }
    // eslint-disable-next-line no-throw-literal
    throw {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      body: errorData, // The error body (if present)
    };
  }

  return response.blob();
};

/**
 * Utility function to download a blob as a file
 *
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The name of the file to download
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
